import React, { useCallback, useEffect, useRef } from 'react';
import { useSnackbar } from 'notistack';

const MANIFEST_URL = '/asset-manifest.json';
const STORAGE_KEY = 'dashboardAssetManifestHash';

function computeManifestHash(manifest: any): string {
  try {
    const mainJs: string | undefined = manifest?.files?.['main.js'];
    const mainCss: string | undefined = manifest?.files?.['main.css'];
    const entrypoints: string[] | undefined = manifest?.entrypoints;
    const basis = JSON.stringify({ mainJs, mainCss, entrypoints });
    /* eslint-disable no-bitwise */
    let hash = 0;
    for (let i = 0; i < basis.length; i += 1) {
      hash = (hash << 5) - hash + basis.charCodeAt(i);
      hash |= 0;
    }
    /* eslint-enable no-bitwise */
    return `${hash}`;
  } catch (e) {
    return `${Date.now()}`;
  }
}

export function useVersionWatcher(intervalMs: number = 60000): void {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const initialisedRef = useRef<boolean>(false);
  const pendingReloadRef = useRef<boolean>(false);

  const checkForUpdate = useCallback(async () => {
    try {
      const res = await fetch(`${MANIFEST_URL}?t=${Date.now()}`, { cache: 'no-store' });
      if (!res.ok) return;
      const manifest = await res.json();
      const newHash = computeManifestHash(manifest);
      const storedHash = localStorage.getItem(STORAGE_KEY);

      if (!initialisedRef.current) {
        localStorage.setItem(STORAGE_KEY, newHash);
        initialisedRef.current = true;
        return;
      }

      if (storedHash && storedHash !== newHash && !pendingReloadRef.current) {
        pendingReloadRef.current = true;

        const actionKey = enqueueSnackbar('Eine neue Version ist verfügbar. Aktualisieren?', {
          variant: 'info',
          persist: true,
          action: (key) => (
            <button
              type="button"
              onClick={() => {
                closeSnackbar(key);
                localStorage.setItem(STORAGE_KEY, newHash);
                window.location.reload();
              }}
              style={{
                border: 'none',
                background: 'transparent',
                color: '#1976d2',
                fontWeight: 600,
                cursor: 'pointer',
                marginLeft: 8,
              }}
            >
              Aktualisieren
            </button>
          ),
        });

        const handleVisibilityChange = () => {
          if (document.visibilityState === 'hidden') {
            localStorage.setItem(STORAGE_KEY, newHash);
            window.location.reload();
          }
        };
        document.addEventListener('visibilitychange', handleVisibilityChange, { once: true });

        setTimeout(() => {
          try { closeSnackbar(actionKey); } catch (e) { /* noop */ }
        }, 120000);
      }
    } catch (e) {
      // Silent fail
    }
  }, [closeSnackbar, enqueueSnackbar]);

  useEffect(() => {
    checkForUpdate();
    const timer = window.setInterval(checkForUpdate, intervalMs);
    return () => {
      window.clearInterval(timer);
    };
  }, [checkForUpdate, intervalMs]);
}

export default useVersionWatcher;

export const VersionWatcherNode: React.FC<{ intervalMs?: number }> = ({ intervalMs = 60000 }) => {
  useVersionWatcher(intervalMs);
  return null;
};


